import { OpenAI } from "../Icons";
import { CloudProviderSettings, type CloudProviderConnection } from "./CloudProviderSettings";
import {
  canRunCodexCloudTask,
  presentCodexSubscriptionConnection,
  type CodexCloudExecutionTarget,
  type CodexSubscriptionConnection,
} from "./CodexCloudSettings.logic";
import { SettingsPageContainer } from "./settingsLayout";

export interface CodexCloudSettingsProps {
  readonly connection?: CodexSubscriptionConnection;
  readonly environmentKind: CodexCloudExecutionTarget;
  readonly environmentLabel: string;
  readonly readOnly?: boolean;
  readonly subscriptionAuthAvailable?: boolean;
  readonly authUnavailableReason?: string;
  readonly onConnect?: () => Promise<void>;
  readonly onDisconnect?: () => Promise<void>;
  readonly onRetry?: () => void;
}

const DEFAULT_CONNECTION: CodexSubscriptionConnection = { status: "disconnected" };

export function CodexCloudSettings({
  connection = DEFAULT_CONNECTION,
  environmentKind,
  environmentLabel,
  readOnly = false,
  subscriptionAuthAvailable = false,
  authUnavailableReason = "This environment cannot manage Codex subscription authentication.",
  onConnect,
  onDisconnect,
  onRetry,
}: CodexCloudSettingsProps) {
  const presentation = presentCodexSubscriptionConnection(connection);
  const cloudReady = canRunCodexCloudTask({ target: environmentKind, connection });
  const cloudTarget = environmentKind === "cloud";
  const executionDescription = cloudTarget
    ? "Use the official Codex CLI in an isolated AgentCore runtime."
    : environmentKind === "local"
      ? "Use the official Codex CLI on this device."
      : "Use the official Codex CLI in this remote environment.";
  return (
    <CloudProviderSettings
      providerName="Codex"
      subscriptionTitle="Codex subscription"
      providerIcon={<OpenAI className="size-5" aria-hidden />}
      providerAccentClassName="border-[#10a37f]/25 bg-[#10a37f]/8"
      sectionId="cloud-agent-codex"
      sectionTitle={cloudTarget ? "Codex cloud agent" : "Codex subscription"}
      sectionTestId="codex-cloud-settings"
      statusTestId="codex-subscription-status"
      connection={connection as CloudProviderConnection}
      presentation={presentation}
      environmentKind={environmentKind}
      environmentLabel={environmentLabel}
      executionDescription={executionDescription}
      subscriptionDescription={presentation.description}
      cloudReady={cloudReady}
      authAvailable={subscriptionAuthAvailable}
      authUnavailableReason={authUnavailableReason}
      loginCommand="codex login --device-auth"
      cloudWarningTitle="Connect Codex before running in Cloud"
      cloudWarningDescription="The Cloud target is selected, but no Codex subscription is connected yet. Local tasks remain available."
      billingDescription={
        cloudTarget ? (
          <>
            Codex model usage belongs to your ChatGPT plan; Fabrica Cloud billing covers only the
            isolated runtime.
          </>
        ) : (
          <>
            Codex model usage belongs to your ChatGPT plan; Fabrica adds no managed runtime charge
            for this target.
          </>
        )
      }
      readOnly={readOnly}
      {...(onConnect ? { onConnect } : {})}
      {...(onDisconnect ? { onDisconnect } : {})}
      {...(onRetry ? { onRetry } : {})}
    />
  );
}

export function CodexCloudSettingsPage(props: CodexCloudSettingsProps) {
  return (
    <SettingsPageContainer>
      <CodexCloudSettings {...props} />
    </SettingsPageContainer>
  );
}
